import { timer, MonoTypeOperatorFunction, Observable } from 'rxjs';
import {
  scan,
  tap,
  switchMapTo,
  takeWhile,
  last,
  takeUntil,
} from 'rxjs/operators';

/**
 * Lets a caller tell exhaustion from the other way this operator fails: `last()`
 * throws `EmptyError` when `forceStop$` completes the poll before its first
 * emission, and the two mean opposite things — one is a fault, the other a
 * cancellation.
 *
 * `lastFailure` carries what the polled source last failed with, for a caller
 * that answers a failed request with a "keep polling" value: without it, a
 * broken endpoint and a genuinely slow one exhaust the attempts identically.
 * The operator never sets it — the caller that swallowed the failure does.
 */
export class PollExhaustedError extends Error {
  constructor(readonly lastFailure: unknown = null) {
    super('Exceeded maxAttempts');
    this.name = 'PollExhaustedError';
  }
}

function attemptsGuardFactory(maxAttempts: number) {
  return (attemptsCount: number) => {
    if (attemptsCount > maxAttempts) {
      throw new PollExhaustedError();
    }
  };
}

/**
 * Why the poll ran out, for a caller that answered failed requests with a
 * "keep polling" value: ten failures and twenty seconds of genuine slowness
 * exhaust the attempts identically, and only `lastFailure` tells them apart.
 *
 * A function rather than two lines at the call site because those two lines are
 * the whole point of carrying the failure, and a Sentry namespace cannot be
 * spied on -- inline, the branch is reachable by no test.
 */
export const pollExhaustionCause = (
  error: PollExhaustedError
): { cause: 'request-failed' | 'still-processing'; httpStatus?: number } => {
  const failure = error.lastFailure as { status?: number } | null;

  return failure
    ? { cause: 'request-failed', httpStatus: failure.status }
    : { cause: 'still-processing' };
};

export function pollWhile<T>(
  pollInterval: number,
  isPollingActive: (res: T) => boolean,
  forceStop$: Observable<any>,
  maxAttempts = Infinity,
  emitOnlyLast = false
): MonoTypeOperatorFunction<T> {
  return (source$) => {
    const poll$ = timer(0, pollInterval).pipe(
      scan((attempts) => ++attempts, 0),
      tap(attemptsGuardFactory(maxAttempts)),
      switchMapTo(source$),
      takeUntil(forceStop$),
      takeWhile(isPollingActive, true)
    );

    return emitOnlyLast ? poll$.pipe(last()) : poll$;
  };
}
